import { native } from '@forest/native-services'
import { removeTrailingZeros } from '@forest/paywall-core'
import { useStore } from '@nanostores/solid'
import { QueryClient, QueryClientProvider } from '@tanstack/solid-query'
import { createEffect, createResource, createSignal, Suspense } from 'solid-js'
import { Toaster } from 'solid-sonner'
import { useChinaAndroidTrialMutation } from '~/hooks/use-china-android-trial-mutation'
import { useSubscribeMutation } from '~/hooks/use-subscribe-mutation'
import { productsReady } from '~/lib/message'
import { platform } from '~/lib/params'
import { m } from '~/paraglide/messages'
import {
  $chinaAndroidTrialEligibility,
  $isReminderViewOpen,
  $isSubscribeButtonReady,
  $isTrialEligible,
  $serviceLevelStatus,
} from '~/store'
import { ErrorDialog } from './error-dialog'
import { LoadingOverlay } from './loading-overlay'
import { ReminderDialog } from './reminder-dialog'
import { ReminderView } from './reminder-view'
import { SubscribedView } from './subscribed-view'

export function SubscribeButton() {
  const queryClient = new QueryClient()
  return (
    <QueryClientProvider client={queryClient}>
      <Suspense>
        <_SubscribeButton />
      </Suspense>
    </QueryClientProvider>
  )
}

export function _SubscribeButton() {
  const serviceLevelStatus = useStore($serviceLevelStatus)
  const isTrialEligible = useStore($isTrialEligible)
  const chinaAndroidTrialEligibility = useStore($chinaAndroidTrialEligibility)
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = createSignal(false)
  const [isAnimLoaded, setIsAnimLoaded] = createSignal(false)
  const [products] = createResource(() => {
    return productsReady.promise
  })

  createEffect(() => {
    if (
      isAnimLoaded() &&
      serviceLevelStatus() &&
      chinaAndroidTrialEligibility() !== 'pending'
    ) {
      $isSubscribeButtonReady.set(true)
    }
  })

  const subscribeMutation = useSubscribeMutation({
    onSuccess: () => setIsSuccessDialogOpen(true),
  })

  const chinaAndroidTrialMutation = useChinaAndroidTrialMutation({
    onSuccess: () => setIsSuccessDialogOpen(true),
  })

  const startTrialOrSubscribe = async () => {
    const products = await productsReady.promise
    const product = products.annualEarlyBird

    if (chinaAndroidTrialEligibility() === 'available') {
      chinaAndroidTrialMutation.mutate()
    } else {
      subscribeMutation.mutate({ product })
    }
  }

  const handleClick = () => {
    const isTrialEligible =
      $isTrialEligible.get() || chinaAndroidTrialEligibility() === 'available'

    native.general.logEvent({
      event_name: 'component_event',
      event_params: {
        type: 'btn',
        component_name: isTrialEligible
          ? 'new_user_paywall_trial'
          : 'new_user_paywall_subscribe',
        app_action: 'click',
        selected_plan: 'annual',
      },
    })

    if (isTrialEligible) {
      $isReminderViewOpen.set(true)
      return
    }

    startTrialOrSubscribe()
  }

  const handleStartTrialFromReminderView = () => {
    startTrialOrSubscribe()
  }

  const buttonMessage = () => {
    if (platform === 'china_android') {
      if (chinaAndroidTrialEligibility() === 'available') {
        return m.onboarding_paywall_start_triasl_btn_cna({
          trial_day_number: 7,
        })
      } else {
        return m.onboarding_paywall_start_subscribe_btn_cna()
      }
    }
    if (isTrialEligible()) {
      return m.btn_try_free_free_ver()
    }
    const price = removeTrailingZeros(
      products()?.annualEarlyBird.price_localized ?? '',
    )
    return m.new_user_yearly_subscription_btn({ discount_yearly_price: price })
  }

  return (
    <>
      <button
        class="group h-[52px] w-full rounded-xl p-px [background-image:linear-gradient(180deg,#C5FF70_0%,#69FF78_45%,#30D3FF_100%)]"
        onClick={handleClick}
        disabled={
          subscribeMutation.isPending || chinaAndroidTrialMutation.isPending
        }
      >
        <span class="flex h-full w-full items-center justify-center rounded-[11px] font-bold text-[#176361] [background-image:linear-gradient(106deg,#C5FF70_13%,#30D3FF_97%)] group-active:[background-image:linear-gradient(90deg,#94D66E_0%,#2BB8D3_100%)]">
          {buttonMessage()}
        </span>
      </button>

      <SubscribedView
        isOpen={isSuccessDialogOpen()}
        isTrial={chinaAndroidTrialEligibility() === 'available'}
        onAnimLoaded={() => setIsAnimLoaded(true)}
      />

      <ReminderView
        onStartTrial={handleStartTrialFromReminderView}
        isLoading={
          subscribeMutation.isPending || chinaAndroidTrialMutation.isPending
        }
      />

      <ErrorDialog />

      <ReminderDialog />

      <LoadingOverlay
        isOpen={
          subscribeMutation.isPending || chinaAndroidTrialMutation.isPending
        }
      />

      <Toaster />
    </>
  )
}
